import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, Image as ImageIcon, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';

const MaterialDetailModal = ({ material, open, onClose, onSuccess, userRole }) => {
  const canCustomerApprove = userRole === 'customer' && material?.admin_approved && material?.status === 'pending';
  const canAdminApprove = userRole === 'admin' && !material?.admin_approved;

  const handleAdminApprove = async () => {
    try {
      await api.post(`/materials/${material.material_id}/admin-approve`);
      toast.success('Material approved for customer review');
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to approve material');
    }
  };

  const handleCustomerAction = async (status) => {
    try {
      await api.patch(`/materials/${material.material_id}`, {
        material_id: material.material_id,
        status,
        feedback: null
      });
      toast.success(`Material ${status}`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error('Failed to update material');
    }
  };

  if (!material) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-start justify-between">
            <div>
              <DialogTitle className="text-xl">{material.name}</DialogTitle>
              <DialogDescription className="mt-1">{material.category}</DialogDescription>
            </div>
            <Badge className={`${
              material.status === 'approved' ? 'bg-green-100 text-green-700' :
              material.status === 'rejected' ? 'bg-red-100 text-red-700' :
              material.admin_approved ? 'bg-blue-100 text-blue-700' :
              'bg-yellow-100 text-yellow-700'
            }`}>
              {material.admin_approved ? (material.status === 'pending' ? 'Awaiting Customer' : material.status) : 'Under Admin Review'}
            </Badge>
          </div>
        </DialogHeader>

        <div className="space-y-6">
          {/* Specifications */}
          <div className="bg-stone-50 border border-stone-200 rounded-sm p-4">
            <h4 className="font-semibold text-sm text-stone-950 mb-3">Specifications</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-stone-600 mb-1">Brand</p>
                <p className="font-medium text-stone-950">{material.brand || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-stone-600 mb-1">Model</p>
                <p className="font-medium text-stone-950">{material.model || 'Not specified'}</p>
              </div>
              <div className="col-span-2">
                <p className="text-stone-600 mb-1">Details</p>
                <p className="font-medium text-stone-950">{material.specifications}</p>
              </div>
              {userRole !== 'engineer' && (
                <div className="col-span-2">
                  <p className="text-stone-600 mb-1">Estimated Cost</p>
                  <p className="text-xl font-bold text-orange-700">₹{material.estimated_cost.toLocaleString('en-IN')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Photos */}
          {material.images && material.images.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-stone-950 mb-3">Product Photos</h4>
              <div className="grid grid-cols-3 gap-3">
                {material.images.map((img, idx) => (
                  <div key={idx} className="aspect-square bg-stone-200 rounded-sm flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-stone-400" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Brochures */}
          {material.brochures && material.brochures.length > 0 && (
            <div>
              <h4 className="font-semibold text-sm text-stone-950 mb-3">Brochures & Documents</h4>
              <div className="space-y-2">
                {material.brochures.map((doc, idx) => (
                  <div key={idx} className="flex items-center space-x-3 p-3 border border-stone-200 rounded-sm">
                    <FileText className="h-5 w-5 text-orange-700" />
                    <span className="text-sm font-medium">Brochure {idx + 1}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Admin Approval Section */}
          {canAdminApprove && (
            <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
              <p className="text-sm text-blue-900 mb-3">
                Review this material submission. Once approved, it will be visible to the customer for their approval.
              </p>
              <Button
                onClick={handleAdminApprove}
                className="w-full bg-orange-700 hover:bg-orange-800 text-white"
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Approve for Customer Review
              </Button>
            </div>
          )}

          {/* Customer Approval Section */}
          {canCustomerApprove && (
            <div className="bg-green-50 border border-green-200 rounded-sm p-4">
              <p className="text-sm text-green-900 mb-4">
                This material has been reviewed by your project team. Please approve or reject based on your preferences.
              </p>
              <div className="flex space-x-3">
                <Button
                  onClick={() => handleCustomerAction('approved')}
                  className="flex-1 bg-green-700 hover:bg-green-800 text-white"
                  data-testid={`approve-material-${material.material_id}`}
                >
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Approve
                </Button>
                <Button
                  onClick={() => handleCustomerAction('rejected')}
                  variant="outline"
                  className="flex-1 border-red-300 text-red-700 hover:bg-red-50"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Reject
                </Button>
              </div>
            </div>
          )}

          {material.status === 'approved' && (
            <div className="bg-green-50 border border-green-200 rounded-sm p-3 text-center">
              <CheckCircle2 className="h-6 w-6 text-green-700 inline-block mr-2" />
              <span className="text-sm font-medium text-green-900">Material Approved</span>
            </div>
          )}

          {material.status === 'rejected' && (
            <div className="bg-red-50 border border-red-200 rounded-sm p-3 text-center">
              <XCircle className="h-6 w-6 text-red-700 inline-block mr-2" />
              <span className="text-sm font-medium text-red-900">Material Rejected</span>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MaterialDetailModal;
